/**
 * TeamLeadHub — Dashboard for team leads to manage their agents.
 *
 * Route: /team-lead/*
 * Tabs: Dashboard | My Agents | Billing
 *
 * Team leads invite agents at $150/mo each (team lead pays $185 first seat).
 * Reuses HubShell + shared navy+gold components from broker-intelligence.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faChartLine,
  faCreditCard,
  faPlus,
  faSpinner,
  faCircle,
  faEnvelope,
  faTrash,
  faUserPlus,
  faDollarSign,
  faCheckCircle,
  faClockRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { HubShell, GoldButton, OutlineButton } from '../broker-intelligence/shared';
import type { HubTab, HubMetric } from '../broker-intelligence/shared';
import SplitPanel from '../broker-intelligence/shared/SplitPanel';
import SearchBar from '../broker-intelligence/shared/SearchBar';
import ListItem from '../broker-intelligence/shared/ListItem';
import { colors, fonts } from '../broker-intelligence/shared/colors';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TeamAgent {
  id: string;
  userId?: string;
  name: string;
  email: string;
  phone?: string;
  status: string;
  joinedAt?: string;
  lastActive?: string;
  plan?: string;
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const TABS: HubTab[] = [
  { key: 'dashboard', label: 'Dashboard', icon: faChartLine },
  { key: 'agents',    label: 'My Agents', icon: faUsers },
  { key: 'billing',   label: 'Billing',   icon: faCreditCard },
];

function getTabFromPath(pathname: string): string {
  const segments = pathname.replace(/\/$/, '').split('/');
  const after = segments[segments.indexOf('team-lead') + 1];
  if (after && TABS.some(t => t.key === after)) return after;
  return 'dashboard';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TeamLeadHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  // Data
  const [agents, setAgents] = useState<TeamAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<TeamAgent | null>(null);

  // Invite form
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [inviting, setInviting] = useState(false);

  // Brokerage context
  const [brokerageId, setBrokerageId] = useState('');

  // Sync tab from URL
  useEffect(() => {
    const tab = getTabFromPath(location.pathname);
    if (tab !== activeTab) setActiveTab(tab);
  }, [location.pathname]);

  const handleTabChange = useCallback((key: string) => {
    setActiveTab(key);
    navigate(`/team-lead/${key}`, { replace: true });
  }, [navigate]);

  // Fetch team members
  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/team-lead/members');
      if (res.data.success) {
        setAgents(res.data.members || []);
        if (res.data.brokerageId) setBrokerageId(res.data.brokerageId);
      }
    } catch (err: any) {
      console.error('Failed to load team members:', err);
      // Fallback: try broker agents endpoint
      try {
        const brokerageRes = await axiosInstance.get('/chat/team/members');
        if (brokerageRes.data.brokerage?.id) {
          setBrokerageId(brokerageRes.data.brokerage.id);
          const agentsRes = await axiosInstance.get(`/broker/agents/${brokerageRes.data.brokerage.id}`);
          if (agentsRes.data.success) {
            setAgents(agentsRes.data.agents.map((a: any) => ({
              id: a.id,
              userId: a.user_id,
              name: a.agent_full_name?.replace(' (Pending Invitation)', '') || '',
              email: a.agent_email,
              phone: a.agent_phone || '',
              status: a.isAuthenticated ? 'active' : 'pending',
              plan: 'team-member-discounted',
            })));
          }
        }
      } catch {}
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  // Invite agent
  const handleInvite = useCallback(async () => {
    if (!inviteData.email || !inviteData.firstName) {
      toast.error('Name and email are required');
      return;
    }
    setInviting(true);
    try {
      const res = await axiosInstance.post('/team-lead/save-agent', {
        agentData: inviteData,
        brokerageId,
      });
      if (res.data.success) {
        toast.success('Agent invited!');
        setInviteData({ firstName: '', lastName: '', email: '', phone: '' });
        setShowInvite(false);
        // Send invitation email
        if (res.data.member?.id) {
          await axiosInstance.post('/team-lead/send-invitation', { memberId: res.data.member.id }).catch(() => {});
        }
        fetchAgents();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to invite agent');
    } finally {
      setInviting(false);
    }
  }, [inviteData, brokerageId, fetchAgents]);

  // Remove agent
  const handleRemove = useCallback(async (agentId: string) => {
    try {
      await axiosInstance.delete(`/team-lead/member/${agentId}`);
      toast.success('Agent removed');
      setSelectedAgent(null);
      fetchAgents();
    } catch {
      toast.error('Failed to remove agent');
    }
  }, [fetchAgents]);

  // Stats
  const stats = useMemo(() => {
    const active = agents.filter(a => a.status === 'active').length;
    const pending = agents.filter(a => a.status !== 'active').length;
    const monthlyCost = 185 + (agents.length * 150);
    return { total: agents.length, active, pending, monthlyCost };
  }, [agents]);

  const filteredAgents = useMemo(() => {
    if (!search) return agents;
    const q = search.toLowerCase();
    return agents.filter(a => a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q));
  }, [agents, search]);

  // Metrics
  const metrics: HubMetric[] = useMemo(() => [
    { icon: faUsers, value: stats.total, label: 'Team Members', color: colors.blue },
    { icon: faCheckCircle, value: stats.active, label: 'Active', color: colors.green },
    { icon: faClockRotateLeft, value: stats.pending, label: 'Pending', color: colors.orange },
    { icon: faDollarSign, value: `$${stats.monthlyCost}`, label: 'Monthly Cost', color: colors.gold2 },
  ], [stats]);

  const breadcrumb = `Team Lead / ${TABS.find(t => t.key === activeTab)?.label || 'Dashboard'}`;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <HubShell
      breadcrumb={breadcrumb}
      brandMiddle="Team"
      homeHref="/agent-portal/dashboard"
      metrics={activeTab === 'dashboard' ? metrics : []}
      actions={
        <GoldButton onClick={() => { setShowInvite(true); setActiveTab('agents'); navigate('/team-lead/agents', { replace: true }); }}>
          <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: 6 }} /> Invite Agent
        </GoldButton>
      }
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    >
      {/* ── Dashboard Tab ─────────────────────────────────────────── */}
      {activeTab === 'dashboard' && (
        <div style={{ padding: 24, background: colors.offBg, height: '100%', overflowY: 'auto' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: colors.navy, margin: '0 0 24px' }}>
            Team Dashboard
          </h2>

          {/* Cost breakdown */}
          <div style={{
            background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24,
            border: `1px solid ${colors.border}`,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 16px', fontFamily: fonts.body }}>
              Monthly Billing Summary
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: colors.muted }}>Your seat (Team Lead)</span>
                <span style={{ fontWeight: 600, color: colors.navy }}>$185.00</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: colors.muted }}>Team members ({stats.total} × $150)</span>
                <span style={{ fontWeight: 600, color: colors.navy }}>${(stats.total * 150).toFixed(2)}</span>
              </div>
              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 16 }}>
                <span style={{ fontWeight: 700, color: colors.navy }}>Total</span>
                <span style={{ fontWeight: 700, color: colors.gold }}>${stats.monthlyCost.toFixed(2)}/mo</span>
              </div>
            </div>
          </div>

          {/* Recent agents */}
          <div style={{
            background: '#fff', borderRadius: 12, padding: 24,
            border: `1px solid ${colors.border}`,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 16px', fontFamily: fonts.body }}>
              Team Members
            </h3>
            {agents.length === 0 ? (
              <p style={{ color: colors.muted, fontSize: 14 }}>No team members yet. Invite your first agent to get started!</p>
            ) : (
              agents.slice(0, 10).map(a => (
                <div key={a.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderBottom: `1px solid ${colors.border}`,
                }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 14, color: colors.navy }}>{a.name}</span>
                    <span style={{ marginLeft: 8, fontSize: 12, color: colors.muted }}>{a.email}</span>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                    background: a.status === 'active' ? 'rgba(39,174,96,0.1)' : 'rgba(243,156,18,0.1)',
                    color: a.status === 'active' ? colors.green : colors.orange,
                  }}>
                    {a.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── My Agents Tab ─────────────────────────────────────────── */}
      {activeTab === 'agents' && (
        <SplitPanel
          left={
            <>
              <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <SearchBar value={search} onChange={setSearch} placeholder="Search agents..." />
                  <button
                    onClick={() => setShowInvite(!showInvite)}
                    style={{
                      background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                      border: 'none', borderRadius: 6, padding: '0 12px',
                      color: colors.navy, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>

                {/* Invite form */}
                {showInvite && (
                  <div style={{
                    background: colors.navy3, borderRadius: 8, padding: 12, marginBottom: 8,
                  }}>
                    {['firstName', 'lastName', 'email', 'phone'].map(field => (
                      <input
                        key={field}
                        type={field === 'email' ? 'email' : 'text'}
                        placeholder={field === 'firstName' ? 'First Name *' : field === 'lastName' ? 'Last Name' : field === 'email' ? 'Email *' : 'Phone'}
                        value={(inviteData as any)[field]}
                        onChange={e => setInviteData(prev => ({ ...prev, [field]: e.target.value }))}
                        style={{
                          width: '100%', background: colors.navy2, border: 'none',
                          borderRadius: 4, padding: '6px 10px', color: colors.white,
                          fontSize: 12, marginBottom: 6, outline: 'none', fontFamily: fonts.body,
                        }}
                      />
                    ))}
                    <button
                      onClick={handleInvite}
                      disabled={inviting}
                      style={{
                        width: '100%', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                        border: 'none', borderRadius: 6, padding: '8px',
                        color: colors.navy, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                      }}
                    >
                      {inviting ? <><FontAwesomeIcon icon={faSpinner} spin /> Inviting...</> : 'Send Invitation'}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading ? (
                  <div style={{ padding: 32, textAlign: 'center', color: colors.muted }}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                  </div>
                ) : filteredAgents.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
                    <FontAwesomeIcon icon={faUsers} style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
                    <div style={{ fontSize: 13 }}>
                      {agents.length === 0 ? 'No team members yet' : 'No matching agents'}
                    </div>
                  </div>
                ) : (
                  filteredAgents.map(agent => (
                    <ListItem
                      key={agent.id}
                      selected={selectedAgent?.id === agent.id}
                      onClick={() => setSelectedAgent(agent)}
                      title={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {agent.name}
                          <FontAwesomeIcon icon={faCircle} style={{
                            fontSize: 7,
                            color: agent.status === 'active' ? colors.green : colors.orange,
                          }} />
                        </span>
                      }
                      subtitle={agent.email}
                      meta={`$150/mo · ${agent.status}`}
                    />
                  ))
                )}
              </div>
            </>
          }
          right={
            selectedAgent ? (
              <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: colors.navy2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: colors.gold, fontWeight: 700, fontSize: 20, fontFamily: fonts.body,
                  }}>
                    {selectedAgent.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, color: colors.navy, fontFamily: fonts.body }}>{selectedAgent.name}</h3>
                    <p style={{ margin: 0, fontSize: 13, color: colors.muted }}>{selectedAgent.email}</p>
                  </div>
                </div>

                <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: `1px solid ${colors.border}`, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, color: colors.muted, marginBottom: 4 }}>Status</div>
                  <span style={{
                    fontSize: 13, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                    background: selectedAgent.status === 'active' ? 'rgba(39,174,96,0.1)' : 'rgba(243,156,18,0.1)',
                    color: selectedAgent.status === 'active' ? colors.green : colors.orange,
                  }}>
                    {selectedAgent.status}
                  </span>
                </div>

                <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: `1px solid ${colors.border}`, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, color: colors.muted, marginBottom: 4 }}>Plan</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: colors.navy }}>Team Member — $150/mo</div>
                </div>

                {selectedAgent.phone && (
                  <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: `1px solid ${colors.border}`, marginBottom: 16 }}>
                    <div style={{ fontSize: 13, color: colors.muted, marginBottom: 4 }}>Phone</div>
                    <div style={{ fontSize: 14, color: colors.navy }}>{selectedAgent.phone}</div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
                  <OutlineButton onClick={() => handleRemove(selectedAgent.id)}>
                    <FontAwesomeIcon icon={faTrash} style={{ marginRight: 6 }} /> Remove from Team
                  </OutlineButton>
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', height: '100%', padding: 40, textAlign: 'center',
              }}>
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 24,
                }}>
                  <FontAwesomeIcon icon={faUsers} style={{ fontSize: 36, color: colors.navy }} />
                </div>
                <h2 style={{ color: colors.navy, fontFamily: fonts.display, fontSize: 28, margin: '0 0 8px' }}>
                  My Team
                </h2>
                <p style={{ color: colors.muted, fontSize: 14, maxWidth: 400, lineHeight: 1.6 }}>
                  Select a team member to view details, or click "Add" to invite a new agent to your team.
                  Each additional team member is $150/month.
                </p>
              </div>
            )
          }
        />
      )}

      {/* ── Billing Tab ────────────────────────────────────────────── */}
      {activeTab === 'billing' && (
        <div style={{ padding: 24, background: colors.offBg, height: '100%', overflowY: 'auto' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: colors.navy, margin: '0 0 24px' }}>
            Billing
          </h2>

          <div style={{
            background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24,
            border: `1px solid ${colors.border}`,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 16px', fontFamily: fonts.body }}>
              Subscription Summary
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${colors.border}` }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: colors.navy }}>Team Lead (Your Seat)</div>
                  <div style={{ fontSize: 12, color: colors.muted }}>Full AI Pro access</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 16, color: colors.navy }}>$185.00/mo</div>
              </div>

              {agents.map((a, i) => (
                <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${colors.border}` }}>
                  <div>
                    <div style={{ fontSize: 14, color: colors.navy }}>{a.name}</div>
                    <div style={{ fontSize: 11, color: colors.muted }}>{a.email} · {a.status}</div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: colors.navy }}>$150.00/mo</div>
                </div>
              ))}

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0 0', borderTop: `2px solid ${colors.navy}` }}>
                <div style={{ fontWeight: 700, fontSize: 18, color: colors.navy }}>Total Monthly</div>
                <div style={{ fontWeight: 700, fontSize: 18, color: colors.gold }}>${stats.monthlyCost.toFixed(2)}</div>
              </div>
            </div>
          </div>

          <div style={{
            background: 'rgba(201,149,42,0.1)', border: `1px solid ${colors.gold}`,
            borderRadius: 8, padding: 16, fontSize: 13, color: colors.gold, lineHeight: 1.6,
          }}>
            <strong>Team Plan Pricing:</strong> Your first seat (Team Lead) is $185/month with full AI Pro features.
            Each additional team member you invite is $150/month. All seats include the full AI Pro feature set:
            CRM, AI Search Portal, AI CMA Reports, AI Lead Coach, and Ask the Broker.
          </div>
        </div>
      )}
    </HubShell>
  );
};

export default TeamLeadHub;
